import { useCallback, useState, useEffect, Fragment, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { object, string, number } from 'yup';
import { Tooltip } from '@mui/material';
import { useFormik } from 'formik';
import {
  useDispatch,
  useSelector,
  updateResource,
  getCustomerSettings,
  getCustomerPaymentMethods,
  saveWithTrackingConfirmation,
  systemSelectors,
} from '../../../state';
import { FormModal } from './FormModal';
import { RadioInput, SelectInput, TextInput } from '../inputs';
import { InfoIcon } from '../../../assets';
import { convertNullFieldsToEmptyString } from '../../../lib';
import { InfoMessage, WarningMessage } from '../InfoMessage';
import { useGetUsersData, useRefresh } from '../../../hooks';
import {
  AccountBalanceType,
  InvoiceFrequencyType,
  PaymentMethodType,
  QbPaymentTermType,
  ReceiptReturnInspectionType,
  SelectOption,
  ShippingSelectionPreferenceType,
  StorageBillingMethodType,
  StorageBillingPeriodType,
  WarehouseStorageCategoryType,
  PickAllocationMethodType,
  InventoryTrackPreferenceType,
} from '../../../types';
import { getCCDisplay } from './creditCardUtils';

const accountBalanceTypes = {
  PRE_PAY: 'Prepay',
  CREDIT_LIMIT: 'CreditLimit',
} satisfies Record<string, AccountBalanceType>;

interface Props {
  open: boolean;
  handleClose: () => any;
  customerId: number;
  setViewVersion: React.Dispatch<React.SetStateAction<number>>;
  // TODO: add type after we convert the paths to typescript
  selectedTab?: string;
}

interface State {
  paymentMethod: PaymentMethodType | '';
  paymentTerm: QbPaymentTermType | '';
  invoiceFrequency: InvoiceFrequencyType | '';
  purchaseLabelsInvoiceFrequency: InvoiceFrequencyType | '';
  shippingSelectionPreference: ShippingSelectionPreferenceType | '';
  defaultPickAndStickMode: 'yes' | 'no';
  defaultShipsWholePallet: 'yes' | 'no';
  canScanAndAddInBulk: 'yes' | 'no';
  directiveOverrideMaxWeight: number | '';
  directiveOverrideMaxHeight: number | '';
  storageLocationPreference: WarehouseStorageCategoryType | '';
  storageBillingPeriod: StorageBillingPeriodType;
  storageBillingMethod: StorageBillingMethodType;
  returnInspectionPreference: ReceiptReturnInspectionType;
  prepayRequired: 'yes' | 'no';
  sendPastDueAlert: 'yes' | 'no';
  autoCharge: 'yes' | 'no';
  canViewAccountingDetails: 'yes' | 'no';
  accountBalanceType: AccountBalanceType | '';
  minBalanceRequired: '';
  minReplenishAmount: '';
  taxEntity: '';
  allocationMethod: PickAllocationMethodType | '';
  manualAllocateOrders: 'yes' | 'no';
  prioritizeFullMoveableUnitPicks: 'yes' | 'no';
  prioritizeUnitOfMeasurePicks: 'yes' | 'no';
  cartRoverMarkOrderReadyToProcess: 'yes' | 'no';
  serialNumberTrackPreference: InventoryTrackPreferenceType | '';
  lotNumberTrackPreference: InventoryTrackPreferenceType | '';
  expirationDateTrackPreference: InventoryTrackPreferenceType | '';
  includePackingSlip: 'yes' | 'no';
  defaultCreditCardPaymentMethodID: number | '';
  defaultAchPaymentMethodID: number | '';
}

const initialState: State = {
  paymentMethod: '',
  paymentTerm: '',
  invoiceFrequency: '',
  purchaseLabelsInvoiceFrequency: '',
  shippingSelectionPreference: '',
  defaultPickAndStickMode: 'no',
  defaultShipsWholePallet: 'no',
  canScanAndAddInBulk: 'no',
  directiveOverrideMaxWeight: '',
  directiveOverrideMaxHeight: '',
  storageLocationPreference: '',
  storageBillingPeriod: 'ReceiptSplitRecurringMonthly',
  storageBillingMethod: 'Location',
  returnInspectionPreference: 'InspectPutAwayGoodDisposeBad',
  prepayRequired: 'no',
  sendPastDueAlert: 'yes',
  autoCharge: 'no',
  canViewAccountingDetails: 'no',
  accountBalanceType: accountBalanceTypes.PRE_PAY,
  minBalanceRequired: '',
  minReplenishAmount: '',
  taxEntity: '',
  allocationMethod: '',
  manualAllocateOrders: 'no',
  prioritizeFullMoveableUnitPicks: 'no',
  prioritizeUnitOfMeasurePicks: 'no',
  cartRoverMarkOrderReadyToProcess: 'yes',
  serialNumberTrackPreference: '',
  lotNumberTrackPreference: '',
  expirationDateTrackPreference: '',
  includePackingSlip: 'no',
  defaultCreditCardPaymentMethodID: '',
  defaultAchPaymentMethodID: '',
};

export function BasicCustomerSettings({
  open,
  handleClose,
  customerId,
  setViewVersion,
  selectedTab,
}: Props) {
  const { classes, cx, css } = useStyles();

  const dispatch = useDispatch();
  const refresh = useRefresh();
  const { isCustomer } = useGetUsersData();

  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(initialState);
  const [warehouseLocationCategoryTypes, setWarehouseLocationCategoryTypes] =
    useState<Array<SelectOption<WarehouseStorageCategoryType>>>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);

  const paymentMethodTypes = useSelector(systemSelectors.paymentMethodTypes);

  const shippingSelectionPreferenceTypes = useSelector(
    systemSelectors.shippingSelectionPreferenceTypes,
  );

  const allWarehouseLocationCategoryTypes = useSelector(
    systemSelectors.warehouseStorageCategoryTypes,
  );

  const storageBillingMethodTypes = useSelector(
    systemSelectors.storageBillingMethodTypes,
  );

  const storageBillingPeriodTypes = useSelector(
    systemSelectors.storageBillingPeriodTypes,
  );

  const receiptReturnInspectionTypes = useSelector(
    systemSelectors.receiptReturnInspectionTypes,
  );

  const paymentTermTypes = useSelector(systemSelectors.paymentTermTypes);

  const invoiceFrequencyTypes = useSelector(
    systemSelectors.invoiceFrequencyTypes,
  );

  const taxEntityTypes = useSelector(systemSelectors.taxEntityType);

  const pickAllocationMethodTypes = useSelector(
    systemSelectors.pickAllocationMethodTypes,
  );

  const serialNumInventoryTrackPreferenceTypes = useSelector(
    systemSelectors.inventoryTrackPreferenceTypes,
  );

  const lotAndExpirationDateInventoryTrackPreferenceTypes = useMemo(() => {
    return serialNumInventoryTrackPreferenceTypes.filter(
      (t) => t.id !== 'EnableOnOutbound' && t.id !== 'DoNotTrack',
    );
  }, [serialNumInventoryTrackPreferenceTypes]);

  // For customer-level settings, exclude DoNotTrack (only valid at item level)
  const customerSerialNumInventoryTrackPreferenceTypes = useMemo(() => {
    return serialNumInventoryTrackPreferenceTypes.filter(
      (t) => t.id !== 'DoNotTrack',
    );
  }, [serialNumInventoryTrackPreferenceTypes]);

  useEffect(() => {
    setWarehouseLocationCategoryTypes(
      allWarehouseLocationCategoryTypes.filter(
        (t) => t.id === 'PickRack' || t.id === 'Floor',
      ),
    );
  }, [allWarehouseLocationCategoryTypes]);

  useEffect(() => {
    if (customerId) {
      (async function () {
        const { data } = await dispatch(getCustomerPaymentMethods(customerId));
        if (data && Array.isArray(data.results)) {
          setPaymentMethods(data.results);
        }
      })();
    }
  }, [customerId, dispatch, open]);

  useEffect(() => {
    (async function () {
      const { data } = await dispatch(getCustomerSettings(customerId));
      if (data) {
        setState((cur) => ({
          ...cur,
          ...convertNullFieldsToEmptyString(data),
          returnInspectionPreference:
            data.returnInspectionPreference || 'InspectPutAwayGoodDisposeBad',
          defaultPickAndStickMode: data.defaultPickAndStickMode ? 'yes' : 'no',
          defaultShipsWholePallet: data.defaultShipsWholePallet ? 'yes' : 'no',
          canScanAndAddInBulk: data.canScanAndAddInBulk ? 'yes' : 'no',
          prepayRequired: data.prepayRequired ? 'yes' : 'no',
          sendPastDueAlert: data.sendPastDueAlert ? 'yes' : 'no',
          autoCharge: data.autoCharge ? 'yes' : 'no',
          canViewAccountingDetails: data.canViewAccountingDetails
            ? 'yes'
            : 'no',
          accountBalanceType:
            data.accountBalanceType ?? accountBalanceTypes.PRE_PAY,
          cartRoverMarkOrderReadyToProcess:
            data.cartRoverMarkOrderReadyToProcess ? 'yes' : 'no',
          prioritizeUnitOfMeasurePicks: data.prioritizeUnitOfMeasurePicks
            ? 'yes'
            : 'no',
          prioritizeFullMoveableUnitPicks: data.prioritizeFullMoveableUnitPicks
            ? 'yes'
            : 'no',
          manualAllocateOrders: data.manualAllocateOrders ? 'yes' : 'no',
          includePackingSlip: data.includePackingSlip ? 'yes' : 'no',
          defaultCreditCardPaymentMethodID:
            data.defaultCreditCardPaymentMethodID,
          defaultAchPaymentMethodID: data.defaultAchPaymentMethodID,
        }));
      }
    })();
  }, [customerId, dispatch]);

  const handleSubmit = useCallback(
    async (values: State) => {
      setSubmitting(true);
      const payload = {
        ...values,
        cartRoverMarkOrderReadyToProcess:
          values.cartRoverMarkOrderReadyToProcess === 'yes',
        defaultPickAndStickMode: values.defaultPickAndStickMode === 'yes',
        defaultShipsWholePallet: values.defaultShipsWholePallet === 'yes',
        canScanAndAddInBulk: values.canScanAndAddInBulk === 'yes',
        prepayRequired: values.prepayRequired === 'yes',
        sendPastDueAlert: values.sendPastDueAlert === 'yes',
        autoCharge: values.autoCharge === 'yes',
        canViewAccountingDetails: values.canViewAccountingDetails === 'yes',
        prioritizeUnitOfMeasurePicks:
          values.prioritizeUnitOfMeasurePicks === 'yes',
        prioritizeFullMoveableUnitPicks:
          values.prioritizeFullMoveableUnitPicks === 'yes',
        manualAllocateOrders: values.manualAllocateOrders === 'yes',
        includePackingSlip: values.includePackingSlip === 'yes',
      };

      try {
        const { error } = await dispatch(
          saveWithTrackingConfirmation(
            updateResource({
              baseUrl: `/customers/${customerId}/basic-settings`,
              payload,
              message: 'Changes saved',
              shouldShowErrorMessage: false,
            }),
            updateResource({
              baseUrl: `/customers/${customerId}/basic-settings`,
              payload: {
                ...payload,
                confirmTrackingPreferenceChange: true,
              },
              message: 'Changes saved',
              shouldShowErrorMessage: false,
            }),
          ),
        );

        if (!error) {
          setViewVersion((cur) => ++cur);
          refresh();
          handleClose();
        }
      } finally {
        setSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, dispatch],
  );

  const creditCards = useMemo(
    () =>
      paymentMethods
        .filter((pm) => pm.type === 'CreditCard')
        .map((pm) => ({ id: pm.id, name: getCCDisplay(pm) })),
    [paymentMethods],
  );
  const achAccounts = useMemo(
    () =>
      paymentMethods
        .filter((pm) => pm.type === 'BankAccount')
        .map((pm) => ({
          id: pm.id,
          name: `${pm.bankName} - ${pm.nameOnAccount}`,
        })),
    [paymentMethods],
  );

  const formik = useFormik<State>({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: getValidationSchema(selectedTab),
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={
        <span
          className={classes.title}
        >{`Update ${selectedTab === 'customer-accounting' ? 'contract' : 'basic'} settings`}</span>
      }
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 486 } }}
      submitting={submitting}
    >
      {selectedTab === 'customer-accounting' ? (
        <>
          {!isCustomer && (
            <>
              <InfoMessage
                message='Changes on the invoice frequency is not cumulative, it will not affect
                retroactive non-invoiced chargeable fees'
              />
              <SelectInput
                name='invoiceFrequency'
                formikProps={formik}
                options={invoiceFrequencyTypes}
                label='Invoice frequency'
                className={classes.input}
                clearable
              />
              <SelectInput
                name='purchaseLabelsInvoiceFrequency'
                formikProps={formik}
                options={invoiceFrequencyTypes}
                label='Purchase labels invoice frequency'
                className={classes.input}
                clearable
              />
              <SelectInput
                name='paymentTerm'
                formikProps={formik}
                options={paymentTermTypes}
                label='Payment terms'
                className={classes.input}
                clearable
              />
              <div className={classes.sectionTitle}>Account replenishment</div>
              <div className={classes.replenishContainer}>
                <InfoMessage message='When the account reaches the minimum balance amount, the account must be replenished.' />
                <div>
                  <RadioInput
                    formikProps={formik}
                    name='accountBalanceType'
                    radiosRow
                    title='Account limit balance type'
                    titleClassName={css({ fontSize: 12, fontWeight: 500 })}
                    size='small'
                    radios={[
                      {
                        label: 'Prepay (credit balance)',
                        value: accountBalanceTypes.PRE_PAY,
                      },
                      {
                        label: 'Credit limit (debit balance)',
                        value: accountBalanceTypes.CREDIT_LIMIT,
                      },
                    ]}
                  />
                </div>
                <div className={classes.replenishTextFields}>
                  <TextInput
                    name='minBalanceRequired'
                    formikProps={formik}
                    label={
                      formik.values.accountBalanceType ===
                      accountBalanceTypes.PRE_PAY
                        ? 'Prepay - account limit minimum balance'
                        : 'Credit limit - account limit min balance amount'
                    }
                    className={classes.input}
                    format='currency'
                  />
                  <TextInput
                    name='minReplenishAmount'
                    formikProps={formik}
                    label='Replenish amount'
                    className={classes.input}
                    format='currency'
                  />
                </div>
              </div>
              <div className={classes.radioInput}>
                <RadioInput
                  formikProps={formik}
                  name='autoCharge'
                  radiosRow
                  title={
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ marginBottom: 8 }}>Auto charge</div>
                      <InfoMessage message=' A default payment method is necessary' />
                    </div>
                  }
                  size='small'
                  radios={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              </div>
              <div className={classes.radioInput}>
                <RadioInput
                  formikProps={formik}
                  name='canViewAccountingDetails'
                  radiosRow
                  title='Reseller - can view account details'
                  size='small'
                  radios={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              </div>
              <div className={classes.radioInput}>
                <RadioInput
                  formikProps={formik}
                  name='sendPastDueAlert'
                  radiosRow
                  title='Send due invoice alert'
                  size='small'
                  radios={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              </div>
              <div className={classes.radioInput}>
                <RadioInput
                  formikProps={formik}
                  name='prepayRequired'
                  radiosRow
                  title='Prepay trucking & drayage'
                  size='small'
                  radios={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              </div>
            </>
          )}
          <SelectInput
            name='paymentMethod'
            formikProps={formik}
            options={paymentMethodTypes}
            label='Payment method'
            className={classes.input}
            clearable
          />
          <SelectInput
            formikProps={formik}
            name='defaultCreditCardPaymentMethodID'
            label='Default Credit Card'
            options={creditCards}
            className={classes.input}
            clearable
          />
          <SelectInput
            formikProps={formik}
            name='defaultAchPaymentMethodID'
            label='Default ACH'
            options={achAccounts}
            className={classes.input}
            clearable
          />
        </>
      ) : isCustomer ? (
        <SelectInput
          formikProps={formik}
          name='returnInspectionPreference'
          label='Return Inspection Preference'
          options={receiptReturnInspectionTypes}
          className={classes.input}
          clearable
        />
      ) : (
        <Fragment>
          <SelectInput
            name='shippingSelectionPreference'
            formikProps={formik}
            options={shippingSelectionPreferenceTypes}
            label='Preferred shipping method'
            className={classes.input}
            clearable
          />
          <SelectInput
            name='taxEntity'
            formikProps={formik}
            options={taxEntityTypes}
            label='International tax entity type'
            className={classes.input}
            clearable
          />
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='cartRoverMarkOrderReadyToProcess'
              radiosRow
              title='Cartrover - Mark order as ready to process'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='defaultPickAndStickMode'
              radiosRow
              title='Global pick and stick mode'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='defaultShipsWholePallet'
              radiosRow
              title='Pallets ship as received'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='canScanAndAddInBulk'
              radiosRow
              title='Can scan and add in bulk'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <div className={classes.inputRow}>
            <TextInput
              name='directiveOverrideMaxHeight'
              formikProps={formik}
              label='Directive max height'
              className={cx(classes.input, css({ marginRight: 24 }))}
              type='number'
            />
            <TextInput
              name='directiveOverrideMaxWeight'
              formikProps={formik}
              label='Directive max weight'
              className={classes.input}
              type='number'
            />
          </div>
          <SelectInput
            formikProps={formik}
            name='storageLocationPreference'
            label='Storage location preference'
            options={warehouseLocationCategoryTypes}
            className={classes.input}
            clearable
          />
          <SelectInput
            formikProps={formik}
            name='storageBillingMethod'
            label={
              <div>
                <div className={classes.flex} style={{ marginBottom: 8 }}>
                  Storage billing method
                  {formik.values.storageBillingMethod && (
                    <Tooltip
                      title={getStorageBillingMethodTooltip(
                        formik.values.storageBillingMethod,
                      )}
                    >
                      <InfoIcon sx={{ fontSize: 18 }} />
                    </Tooltip>
                  )}
                </div>
                <WarningMessage
                  message='Changes to this setting can result in billing errors.'
                  className={classes.selectWarningMsg}
                />
              </div>
            }
            options={storageBillingMethodTypes}
            className={classes.input}
            clearable
            required
            slotProps={warningMessageSelectSlotProps}
          />
          <SelectInput
            formikProps={formik}
            name='storageBillingPeriod'
            label={
              <div>
                <div style={{ marginBottom: 8 }}>Storage billing period</div>
                <WarningMessage
                  message='Changes to this setting can result in billing errors.'
                  className={classes.selectWarningMsg}
                />
              </div>
            }
            options={storageBillingPeriodTypes}
            className={classes.input}
            clearable
            required
            slotProps={warningMessageSelectSlotProps}
          />
          <SelectInput
            formikProps={formik}
            name='returnInspectionPreference'
            label='Return Inspection Preference'
            options={receiptReturnInspectionTypes}
            className={classes.input}
            clearable
          />
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='prioritizeUnitOfMeasurePicks'
              radiosRow
              title='Prioritize unit of measure picks'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='prioritizeFullMoveableUnitPicks'
              radiosRow
              title='Prioritize full moveable unit picks'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='manualAllocateOrders'
              radiosRow
              title='Manual allocate orders'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <div className={classes.radioInput}>
            <RadioInput
              formikProps={formik}
              name='includePackingSlip'
              radiosRow
              title='Include packing slip'
              size='small'
              radios={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
          <SelectInput
            formikProps={formik}
            name='allocationMethod'
            label='Allocation method'
            options={pickAllocationMethodTypes}
            className={classes.input}
            clearable
          />
          <SelectInput
            formikProps={formik}
            name='serialNumberTrackPreference'
            label='Serial number track preference'
            options={customerSerialNumInventoryTrackPreferenceTypes}
            className={classes.input}
            clearable
          />
          <SelectInput
            formikProps={formik}
            name='lotNumberTrackPreference'
            label='Lot number track preference'
            options={lotAndExpirationDateInventoryTrackPreferenceTypes}
            className={classes.input}
            clearable
          />
          <SelectInput
            formikProps={formik}
            name='expirationDateTrackPreference'
            label='Expiration date track preference'
            options={lotAndExpirationDateInventoryTrackPreferenceTypes}
            className={classes.input}
            clearable
          />
        </Fragment>
      )}
    </FormModal>
  );
}

const getStorageBillingMethodTooltip = (
  storageBillingMethod: StorageBillingMethodType,
) => {
  switch (storageBillingMethod) {
    case 'CubicFeet':
      return 'Continue to the fee settings and add a customized fee';
    case 'Location':
      return 'Continue to the warehouse settings to customize the storage rates';
    case 'MoveableUnit':
      return 'Continue to the material settings to customize the storage rates';
    default:
      return '';
  }
};

const warningMessageSelectSlotProps = {
  inputLabel: {
    style: { position: 'static' as const },
  },
  input: {
    style: { marginTop: 0 },
  },
};

const useStyles = makeStyles({ name: { BasicCustomerSettings } })((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  sectionTitle: {
    color: '#000000',
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  input: {
    marginBottom: 16,
    width: '100%',
  },
  radioInput: {
    marginTop: 23,
  },
  replenishContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  replenishTextFields: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  selectWarningMsg: {
    marginBottom: 0,
  },
}));

function getValidationSchema(selectedTab: string | undefined) {
  if (selectedTab === 'customer-accounting') {
    return object().shape({
      paymentMethod: string().required('A default payment type is required.'),
      defaultCreditCardPaymentMethodID: number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        )
        .when('paymentMethod', {
          is: 'CreditCard',
          then: (schema) =>
            schema.required('A default CC is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      defaultAchPaymentMethodID: number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        )
        .when('paymentMethod', {
          is: 'ACH',
          then: (schema) =>
            schema.required('A default ACH is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
    });
  } else {
    return object().shape({
      storageBillingMethod: string().required(
        'A storage billing method is required.',
      ),
      storageBillingPeriod: string().required(
        'A storage billing period is required.',
      ),
    });
  }
}
